import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';
import { Loader2 } from 'lucide-react';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 rounded-lg text-sm font-medium transition-all duration-200 focus-visible:outline-none focus-visible:shadow-glow-focus disabled:pointer-events-none disabled:opacity-50 active:scale-[0.97] motion-reduce:duration-0',
  {
    variants: {
      variant: {
        default:
          'bg-brand-600 text-white hover:bg-brand-700 shadow-glow hover:shadow-glow-strong hover:scale-[1.02]',
        glow:
          'bg-transparent text-brand-600 shadow-glow hover:shadow-glow-strong hover:bg-brand-500/5',
        secondary:
          'bg-surface-2 text-foreground hover:bg-surface-1 border border-border shadow-soft',
        ghost: 'text-foreground hover:bg-surface-2',
        danger:
          'bg-[hsl(var(--status-error))] text-white hover:opacity-90 shadow-soft hover:shadow-soft-md',
        outline:
          'border border-silver-300 dark:border-silver-600 bg-transparent text-foreground hover:bg-surface-2',
      },
      size: {
        sm: 'h-9 px-3 text-xs',
        default: 'h-11 px-4',
        lg: 'h-12 px-6 text-base',
        icon: 'h-11 w-11',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  loading?: boolean;
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, loading, children, disabled, asChild, ...props }, ref) => {
    const classes = cn(buttonVariants({ variant, size }), className);

    if (asChild && React.isValidElement(children)) {
      return React.cloneElement(children as React.ReactElement<any>, {
        className: cn(classes, (children.props as any).className),
        ref,
        ...props,
      });
    }

    return (
      <button
        className={classes}
        ref={ref}
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        {...props}
      >
        {loading && <Loader2 className="h-4 w-4 animate-spin" />}
        {children}
      </button>
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
